<template>
  <div>
    <v-card class="my-1">
      <v-card-title>CP8D</v-card-title>
      <v-row class="px-4">
        <v-col>
          <v-autocomplete
            v-model="selectedYear"
            v-validate="'required'"
            label="Year"
            :items="years"
            clearable
            hide-selected
            :error-messages="errors.collect('selectedYear')"
            data-vv-name="selectedYear"  
            data-vv-as="year"
          />
          <v-autocomplete
            v-model="selectedGroups"
            :items="groupOptions"
            label="Company Groups"
            item-text="text"
            item-value="value"
            multiple
            chips
            small-chips
            deletable-chips
            hide-selected
            clearable
          />
          <v-autocomplete
            v-model="selectedCompanies"
            v-validate="'required'"
            label="Companies"
            item-text="name"
            item-value="id"
            :items="companyOptions"
            clearable
            multiple
            hide-selected 
            chips
            small-chips
            deletable-chips
            :error-messages="errors.collect('selectedCompanies')"
            data-vv-name="selectedCompanies"  
            data-vv-as="companies"
          />
        </v-col>
  
        <v-col
          cols="2"
          class="ma-auto"
        >
          <v-row
            v-if="loading" 
            class="text-center"
          >
            <v-col cols="12">
              <div>
                <span class="font-weight-bold">Downloading</span>
              </div>
              <v-progress-circular
                :rotate="360"
                :size="50"
                :width="5"
                :value="progressbar"
                color="teal"
              >
                {{ progressbar }}
              </v-progress-circular>
            </v-col>
          </v-row>
      
          <v-row class="text-center">
            <v-col cols="12">
              <v-btn
                class="mx-3"
                color="primary"
                :loading="loading"
                @click="generateCp8dEmployee"
              >
                generate
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card>
  </div>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      years: {
        type: Array,
        required: true,
      },
      companies: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        loading: false,
        selectedGroups: [],
        selectedCompanies: [],
        progressbar: 0,
        selectedYear: '',
      }
    },
    computed: {
      groupOptions () {
        if (!this.companies) return []
        const groups = new Set()

        this.companies.forEach(company => {
          const groupVal = company.organization_lookup_uid
          if (!groupVal) return

          groups.add(company.group)
        })

        return Array.from(groups).map(group => ({
          text: `${(group || {}).value} Group`,
          value: group.lookup_uid,
        }))
      },
      filteredCompanies () {
        if (!this.selectedGroups.length) return this.companies

        return (this.companies || []).filter(company =>
          this.selectedGroups.includes(company.organization_lookup_uid)
        )
      },
      companyOptions () {
        const companies = (this.filteredCompanies || []).map(company => ({
          name: company.name,
          id: company.id,
        }))

        return [
          { name: 'All Companies', id: 'ALL' },
          ...companies,
        ]
      },
    },
    watch: {
      selectedGroups () {
        const validIds = (this.filteredCompanies || []).map(company => company.id)

        if (this.selectedCompanies.includes('ALL')) {
          this.selectedCompanies = [...validIds]
        } else {
          this.selectedCompanies = this.selectedCompanies.filter(id =>
            validIds.includes(id)
          )
        }
      },
      selectedCompanies (newVal) {
        const last = newVal[newVal.length - 1]

        // If last selected is 'ALL', keep only 'ALL'
        if (last === 'ALL' && newVal.length > 1) {
          // Replace with only 'ALL' after current update cycle
          this.$nextTick(() => {
            this.selectedCompanies = ['ALL']
          })
        } else if (newVal.includes('ALL') && newVal.length > 1) {
          // Remove 'ALL' if other company is selected after it
          this.$nextTick(() => {
            this.selectedCompanies = newVal.filter(id => id !== 'ALL')
          })
        }
      },
    },
    methods: {
      generateCp8dEmployee () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.loading = true

            this.$api.payrollReports().employeeCp8d(this.prepareFormData(), {
              onDownloadProgress: (progressEvent) => {
                this.progressbar = (Math.floor((progressEvent.loaded * 100) / progressEvent.total))
              },
            })
              .then(res => {
                let blob = new Blob([res.data], { type: 'application/zip' })
                let link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.setAttribute('download', 'CP8D_' + this.selectedYear + '.zip') 
                link.click()
                this.loading = false
                this.progressbar = 0

                this.$snack({ message: 'Report downloaded successfully' })
              })
              .catch(error => {
                this.loading = false
                if (error.response.status === 400) {
                  this.$snack({ 
                    color: 'error',
                    message: 'No record found', 
                  })
                }
              })
          })
      },
      prepareFormData () {
        return {
          year: this.selectedYear,
          companies: this.selectedCompanies.includes('ALL') ? (
            this.filteredCompanies.filter(company => {
              return company.id !== 'ALL'
            }).map(company => {
              return company.id
            })
          ) : this.selectedCompanies }
      },
    },
  }
</script>
